import { useState, type RefObject } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  Button,
  Field,
  FormDrawer,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TableIdentity,
  type DataTableColumn,
} from "@h2j-data-team/ui";
import type { QlikEvaluationRankingItem } from "@repo/shared";

import { qlikEvaluationRankingQueryOptions } from "@/features/app-analysis/api";
import { connectionsQueryOptions } from "@/features/qlik-connections/api";
import { useFormatDate } from "@/hooks/use-format-date";
import { formatMiB, formatQlikSeconds, formatTimingMs } from "@/lib/qlik-evaluation-format";

import { loadErrorState } from "./load-error";
import { ReadOnlyTable } from "./read-only-table";

/**
 * Cross-app view of Qlik's evaluation: the latest completed one per app on a
 * connection, slowest cold sheet first. Only whole-app and whole-sheet numbers
 * appear here — Qlik's per-object times are a sheet's batch time attributed to
 * its objects, which ranks nothing.
 */
export function QlikEvaluationRankingDrawer({
  open,
  onOpenChange,
  defaultConnectionId,
  restoreFocusRef,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultConnectionId?: string;
  /** The button that opened the drawer, which gets the focus back. */
  restoreFocusRef: RefObject<HTMLElement | null>;
}) {
  const { t } = useTranslation("analyses");
  const { t: tc } = useTranslation("common");
  const { formatDate } = useFormatDate();
  const [picked, setPicked] = useState("");
  const connectionId = picked || defaultConnectionId || "";

  const { data: connections } = useQuery({ ...connectionsQueryOptions, enabled: open });
  const { data, isLoading, isError, error, refetch } = useQuery({
    ...qlikEvaluationRankingQueryOptions(connectionId),
    enabled: open && !!connectionId,
  });

  const columns: DataTableColumn<QlikEvaluationRankingItem>[] = [
    {
      id: "app",
      header: t("ranking.colApp"),
      rowHeader: true,
      rowLink: true,
      variant: "identity",
      cell: (item) => (
        <TableIdentity
          name={
            item.analysisId ? (
              <Link
                to="/analyses/$analysisId"
                params={{ analysisId: item.analysisId }}
                className="hover:underline"
                onClick={() => onOpenChange(false)}
              >
                {item.appName}
              </Link>
            ) : (
              item.appName
            )
          }
          detail={
            t("ranking.appMeta", { sheets: item.sheetCount, objects: item.objectCount }) +
            (item.notEvaluatedCount > 0
              ? ` · ${t("ranking.notEvaluated", { count: item.notEvaluatedCount })}`
              : "")
          }
          detailMono={false}
        />
      ),
    },
    // Absence, not zero: an evaluation without a published sheet or an openApp
    // block measured nothing here, and "0 ms" would read as an instant app.
    {
      id: "cold",
      header: t("ranking.colCold"),
      align: "end",
      variant: "numeric",
      width: 145,
      cell: (item) =>
        item.slowestSheetSeconds === null ? (
          <span className="text-faint">—</span>
        ) : (
          formatQlikSeconds(item.slowestSheetSeconds)
        ),
    },
    {
      id: "openApp",
      header: t("ranking.colOpenApp"),
      align: "end",
      variant: "numeric",
      width: 110,
      cell: (item) =>
        item.openAppMs === null ? (
          <span className="text-faint">—</span>
        ) : (
          <span className="text-dim">{formatTimingMs(item.openAppMs)}</span>
        ),
    },
    {
      id: "memory",
      header: t("ranking.colMemory"),
      align: "end",
      variant: "numeric",
      width: 110,
      cell: (item) => <span className="text-dim">{formatMiB(item.documentSizeMiB)}</span>,
    },
    {
      id: "evaluated",
      header: t("ranking.colEvaluated"),
      align: "end",
      variant: "numeric",
      width: 120,
      cell: (item) => <span className="text-dim">{formatDate(item.evaluatedAt)}</span>,
    },
  ];

  return (
    <FormDrawer
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={t("ranking.title")}
      description={t("ranking.description")}
      closeLabel={tc("close")}
      restoreFocusRef={restoreFocusRef}
      footer={
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          {t("ranking.close")}
        </Button>
      }
    >
      <div className="space-y-4">
        <Field label={t("ranking.connectionLabel")}>
          {(control) => (
            <Select value={connectionId} onValueChange={setPicked}>
              <SelectTrigger {...control}>
                <SelectValue placeholder={t("ranking.connectionPlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {connections?.map((conn) => (
                  <SelectItem key={conn.id} value={conn.id}>
                    {conn.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>

        {!connectionId ? (
          <p className="text-sm text-dim">{t("ranking.pickConnection")}</p>
        ) : (
          <>
            <ReadOnlyTable
              label={t("ranking.title")}
              columns={columns}
              rows={data ?? []}
              getRowId={(item) => item.evaluationRowId}
              loading={isLoading}
              error={
                isError
                  ? loadErrorState(error, t("ranking.failedToLoad"), tc("error.supportCode"))
                  : null
              }
              onRetry={() => void refetch()}
              empty={{
                title: t("ranking.empty.title"),
                description: t("ranking.empty.description"),
              }}
              minWidth={640}
            />
            {!!data?.length && (
              <p className="text-[12px] leading-relaxed text-dim">{t("ranking.caveat")}</p>
            )}
          </>
        )}
      </div>
    </FormDrawer>
  );
}
